import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import last from 'lodash/last';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import findIndex from 'lodash/findIndex';
import every from 'lodash/every';
import debounce from 'lodash/debounce';
import Toggle from 'react-toggle';
import styled from 'styled-components';
import Dots from 'react-activity/lib/Dots';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Select } from '../shared/styled-components';
import ostyles from '../shared/styles';
import { authManager } from '../../../../helpers/managers';
import { minLengthValidator, maxLengthValidator } from '../../../../helpers/validators';
import { getAudioDuration } from '../../../../helpers/episodes';
import {
  canUserAccessFeature,
  getAllEpisodesCountV2,
  getSoundcastsManagedCountV2,
} from '../../../../helpers/userinfoHelper';
import Colors from '../../../../styles/colors';
import { RowDivision, BaseButton, LoadingWrapper } from '../../../../styles/styled';
import BeforeUnload from '../../../../components/BeforeUnload';
import ValidatedInput from '../../../../components/inputs/validatedInput';
import Smartuploader from '../../../../components/Smartuploader';
import Loading from '../../../../components/Loading';

class BulkEpisode extends Component {
  constructor(props) {
    super(props);

    this.state = {
      creatorID: '',
      soundcastId: '',
      soundcast: null,
      episodes: [],
      saving: false,
      dirty: false,
      uploads: {},
    };

    // holds how many episodes have been added locally
    this.addedEpisodeCount = 0;

    this.onChangeSoundcastId = this.onChangeSoundcastId.bind(this);
    this.onEpisodeTitleChange = this.onEpisodeTitleChange.bind(this);
    this.onEpisodeStatusChange = this.onEpisodeStatusChange.bind(this);
    this.onDragEpisodeEnd = this.onDragEpisodeEnd.bind(this);
    this.onEpisodeFileAdded = this.onEpisodeFileAdded.bind(this);
    this.onEpisodeFileProcess = this.onEpisodeFileProcess.bind(this);
    this.onEpisodeFileRemove = this.onEpisodeFileRemove.bind(this);
    this.onEpisodeFileNotesProcess = this.onEpisodeFileNotesProcess.bind(this);
    this.onEpisodeFileNotesRemove = this.onEpisodeFileNotesRemove.bind(this);
    this.onShowNotesUploader = this.onShowNotesUploader.bind(this);
    this.onRemoveEpisodeNotes = this.onRemoveEpisodeNotes.bind(this);
    this.saveEpisodes = this.saveEpisodes.bind(this);
    this.saveTitleEpisode = this.saveTitleEpisode.bind(this);
  }

  componentDidMount() {
    const { userInfo } = this.props;

    // if they got here eventho they are not prouser, redirect them
    if (!canUserAccessFeature('EPISODE_BULKUPLOAD')) {
      return this.props.history.push('/dashboard/soundcasts');
    }

    const soundcastId = this.getDefaultSoundcastId();
    if (userInfo && !isEmpty(soundcastId)) {
      // console.log('mound soundcastId', soundcastId);
      this.changeSoundcastId(soundcastId);
    }

    // check if there is an authenticated user
    // if so, give them access
    authManager.onAuthStateChanged(async user => {
      if (user) {
        this.setState({
          creatorID: user.uid,
        });
      }
    });
  }

  getDefaultSoundcastId() {
    const { userInfo = {}, soundcastsManaged = [] } = this.props;

    if (isEmpty(userInfo) || isEmpty(soundcastsManaged)) {
      return '';
    }

    if (this.props.location.state && this.props.location.state.soundcastID) {
      return this.props.location.state.soundcastID;
    }

    return soundcastsManaged[0].id;
  }

  getSoundcastById(soundcastId) {
    const { soundcastsManaged = [] } = this.props;
    if (!isEmpty(soundcastsManaged)) {
      const soundcast = soundcastsManaged.find(soundcast => isEqual(soundcast.id, soundcastId));
      if (soundcast) {
        return soundcast;
      }
    }

    return {};
  }

  reOrderEpisodes(episodes) {
    return episodes.map((episode, index) => {
      episode.index = index;
      return episode;
    });
  }

  changeSoundcastId(soundcastId) {
    this.setState({
      soundcast: this.getSoundcastById(soundcastId),
      soundcastId,
    });
  }

  onChangeSoundcastId(e) {
    this.changeSoundcastId(e.target.value);
  }

  onEpisodeTitleChange(index) {
    return e => {
      const title = e.target.value;
      let { episodes } = this.state;
      if (!isEmpty(episodes[index])) {
        let { id: episodeId, titleHandler } = episodes[index];
        episodes[index].title = title;
        this.setState({ episodes }, () => {
          titleHandler(title, episodeId);
        });
      }
    };
  }

  saveTitleEpisode(title, episodeId) {
    // console.log('Saving title', title, episodeId);
    this.updateEpisode(episodeId, {
      title,
      forcesave: true,
    });
  }

  onEpisodeStatusChange(index) {
    return () => {
      let { episodes } = this.state;
      if (!isEmpty(episodes[index])) {
        let episode = episodes[index];
        this.updateEpisode(episode.id, {
          public: !episode.public,
        });
      }
    };
  }

  onDragEpisodeEnd(result) {
    if (!result.destination) {
      return;
    }

    const { index: sourceIndex, droppableId: sourceDroppableId } = result.source;
    const { index: destIndex, droppableId: destDroppableId } = result.destination;

    if (isEqual(destDroppableId, sourceDroppableId) && isEqual(destIndex, sourceIndex)) {
      return;
    }

    const episodes = Array.from(this.state.episodes);
    const [removed] = episodes.splice(sourceIndex, 1);
    episodes.splice(destIndex, 0, removed);
    const newEpisodes = episodes.map((episode, index) => ({
      ...episode,
      index: episodes.length - 1 - index,
    }));

    this.setState({ episodes: newEpisodes, dirty: true }, () => {
      const requests = newEpisodes.map(episode => this.preSaveEpisode(episode));
      Promise.all(requests);
    });
  }

  onEpisodeFileAdded() {
    return new Promise(resolve => {
      const soundcastCount = getSoundcastsManagedCountV2();
      // increase episode count by 1, evertime a new episode is added
      const baseEpisodesCount = getAllEpisodesCountV2();
      const incrementBy = 1;
      let episodesCount = baseEpisodesCount + this.addedEpisodeCount + incrementBy;
      const isExceeded = this.props.hasExceededPlanLimit(soundcastCount, episodesCount);
      if (isExceeded) {
        this.props.modalHandler({
          show: true,
          upgrade: true,
          title: 'Upgrade for additional track slots',
          content: `You have exceeded the maximum limit on your current plan. Please upgrade to access more track slots.`,
        })();

        return resolve(false);
      }

      // increase add file count
      this.addedEpisodeCount += 1;

      resolve(true);
    });
  }

  onEpisodeFileProcess(error, filepond) {
    if (error) {
      return console.error('Bulk upload error: unable to process episode file', error);
    }

    // add the episode to state
    const { id: localId, serverId, source } = filepond;
    // console.log(filepond);

    if (isEmpty(serverId)) {
      return console.error(
        `Bulk upload error: unable to process episode with empty serverId of file ${
          source.name || localId
        }`
      );
    }

    console.warn(`DEBUG: BulkEpisode onEpisodeFileProcess serverId ${JSON.stringify(serverId)}`);

    const { episodeId } = JSON.parse(serverId);
    if (isEmpty(episodeId)) {
      return console.error(
        `Bulk upload error: unable to process episode with empty episodeId of file ${
          source.name || localId
        }`
      );
    }

    getAudioDuration(filepond.file).then(duration => {
      this.showEpisode(episodeId, {
        localId,
        duration,
        originalName: source.name,
      });
    });
  }

  getNameWithoutExtension(filename) {
    return filename.split('.').slice(0, -1).join('.') || filename + '';
  }

  onEpisodeFileRemove(error, filepond) {
    if (error) {
      return console.error('Bulk upload error: unable to remove episode file', error);
    }

    // remove the episode from state
    const episode = this.state.episodes.find(item => item.localId === filepond.id);
    if (isEmpty(episode)) {
      return console.error(`Bulk upload error: unable to remove file ${filepond.id}`);
    }

    const episodeId = episode.id;
    this.removeEpisode(episodeId, () => {
      // removed the saved episode here
      this.deleteEpisode(episodeId);
    });
  }

  onEpisodeFileNotesProcess({ id }) {
    return (error, filepond) => {
      if (error) {
        return console.error('Bulk upload error: unable to process episode file notes', error);
      }

      // add the episode to state
      const { serverId, filename } = filepond;
      if (!isEmpty(serverId)) {
        const { url: notesUrl } = JSON.parse(serverId);
        this.updateEpisode(id, {
          notesUrl,
          showNotesUploader: false,
          notesBasename: filename,
        });
      }
    };
  }

  onEpisodeFileNotesRemove({ id }) {
    return filepond => {
      const { serverId } = filepond;
      if (!isEmpty(serverId)) {
        this.updateEpisode(id, {
          notesUrl: '',
        });
      }
    };
  }

  onShowNotesUploader({ id }) {
    return () => {
      this.updateEpisode(id, {
        showNotesUploader: true,
        autosave: false,
      });
    };
  }

  onRemoveEpisodeNotes(episode) {
    return async () => {
      try {
        // console.log('Removing notes', episode);
        await this.updateEpisode(episode.id, {
          removingNotes: true,
          autosave: false,
        });

        await Axios.delete('/api/uploadv2', {
          params: {
            url: episode.notesUrl,
          },
        });

        this.updateEpisode(episode.id, {
          showNotesUploader: false,
          notesBasename: '',
          notesUrl: '',
          removingNotes: '',
        });
      } catch (err) {
        console.error('Bulk upload error removed bulk episode notes', err.message);
      }
    };
  }

  showModal(metadata = {}) {
    this.props.modalHandler({
      show: true,
      ...metadata,
    })();
  }

  showEpisode(id, data) {
    const { autosave = true, ...restdata } = data;
    const targetref = this.fileuploaderRef;
    const uploaderRef = targetref.getUploaderRef('file');
    const uploadedFiles = uploaderRef.getPond().getFiles();
    const orderedFilesLocalIds = uploadedFiles.map(file => file.id);

    // get the index of the reversed orderedFilesEpisode, to make the bottom
    // item as the least index value, which affects the index in firebase
    const index = findIndex(orderedFilesLocalIds.reverse(), localId =>
      isEqual(localId, restdata.localId)
    );
    const episode = {
      index,
      id,
      title: this.getNameWithoutExtension(restdata.originalName),
      titleHandler: debounce(this.saveTitleEpisode, 500),
      public: false,
      episodeUrl: `https://app.mysoundwise.com/tracks/${id}.mp3`,
      notesUrl: '',
      showNotesUploader: false,
      notesBasename: '',
      ...restdata,
    };

    this.setState(
      ({ episodes }) => {
        // push to final array
        const oldEpisodes = [...episodes, episode];

        // the order of the uploaded files should follow the order of
        // the upload list from filepond, this is for displaying purposes
        return { episodes: orderBy(oldEpisodes, 'index', 'desc'), dirty: true };
      },
      () => {
        if (autosave) {
          // do the auto save of episode here
          // when the upload of audio initiated
          this.preSaveEpisode(episode);
        }
      }
    );
  }

  updateEpisode(id, data) {
    return new Promise(resolve => {
      const { episodes } = this.state;
      let episodeIndex = findIndex(episodes, episode => isEqual(episode.id, id));
      if (episodeIndex > -1) {
        const oldEpisode = episodes[episodeIndex];
        const { autosave = true, forcesave = false, ...restdata } = data;
        const episode = {
          ...oldEpisode,
          ...restdata,
        };

        if (!isEqual(episode, oldEpisode) || forcesave) {
          // console.log('updateEpisode', id, data, autosave, episode);
          episodes[episodeIndex] = episode;
          this.setState({ episodes, dirty: true }, () => {
            if (autosave || forcesave) {
              // do the auto update of episode here
              // if permitted
              this.preSaveEpisode(episode);
            }

            resolve();
          });
        }
      }
    });
  }

  removeEpisode(id, cb = () => {}) {
    const { episodes } = this.state;
    if (!isEmpty(episodes)) {
      let filteredEpisodes = episodes.filter(episode => !isEqual(episode.id, id));

      // only when an item was filtered
      if (!isEqual(episodes, filteredEpisodes)) {
        // update add file count
        this.addedEpisodeCount -= 1;

        // rearrange episode indexes
        filteredEpisodes = filteredEpisodes.map((episode, index) => {
          episode.index = index;
          return episode;
        });

        const dirty = !isEmpty(filteredEpisodes);
        this.setState({ episodes: filteredEpisodes, dirty }, cb);
      }
    }
  }

  areAllEpisodesValid() {
    const { episodes } = this.state;
    if (isEmpty(episodes)) {
      return false;
    }

    // check every episode if valid
    // mostly just the title
    return every(
      episodes.map(({ title }) => {
        if (isEmpty(title)) {
          return false;
        }

        return true;
      }),
      Boolean
    );
  }

  preSaveEpisode(episode = {}) {
    return new Promise(async (resolve, reject) => {
      try {
        await this.saveEpisodeInternal(episode);
        resolve();
      } catch (err) {
        console.error('Error: There was a problem pre-saving episode', err);
        this.showModal({
          content: 'Something went wrong when saving the track. Please try again.',
        });

        reject(err);
      }
    });
  }

  saveEpisodeInternal(episode = {}, soundcast = {}) {
    console.log('Saving episodes', episode, soundcast);
    const { soundcastId, soundcast: oldsoundcast } = this.state;

    return this.props.realSaveEpisode(episode, {
      ...oldsoundcast,
      ...soundcast,
      soundcastId,
    });
  }

  saveEpisodes(isPublished = false) {
    return async () => {
      try {
        // before we proceed verify the required props first
        const canProceed = this.areAllEpisodesValid();
        if (!canProceed) {
          return this.showModal({
            content: 'Please fill in all the required fields to continue.',
          });
        }

        const { soundcastId, episodes, uploads = {} } = this.state;
        for (const episode of episodes) {
          this.setState({
            uploads: {
              ...uploads,
              [episode.id]: 'uploading',
            },
          });

          await this.saveEpisodeInternal({
            ...episode,
            isPublished,
            complete: true,

            // attach final save param from the last episode
            // to trigger soundcast related updates on the parent component
            finalsave: isEqual(episode, last(episodes)),
            frombulk: true,
          });

          this.setState({
            uploads: {
              ...uploads,
              [episode.id]: 'completed',
            },
          });
        }

        this.setState({ dirty: false }, () => {
          const suffix = isPublished ? 'published' : 'saved as draft';
          this.showModal({
            content: `Track(s) has been successfully ${suffix}.`,
            onClose: () => this.props.history.push(`/dashboard/soundcast/${soundcastId}`),
          });
        });
      } catch (err) {
        console.error('Error: There was a problem saving bulk episodes', err);
        this.showModal({
          content: 'Something went wrong when saving bulk tracks. Please try again.',
        });
      }
    };
  }

  deleteEpisode(episodeId) {
    this.props.onDeleteEpisode(episodeId, this.state.soundcastId);
  }

  renderEpisodeUploadFooter(episode) {
    const { showNotesUploader, notesUrl } = episode;
    const notesUploaderTriggerHelper = !showNotesUploader
      ? 'pdf, png or jpeg file'
      : 'only accepts pdf, png or jpeg file';

    const triggerBtn =
      !showNotesUploader && isEmpty(notesUrl) ? (
        <AddNotesButton
          className="btn btn-small"
          onClick={this.onShowNotesUploader(episode)}
          primaryColor={Colors.mainWhite}
          textColor={Colors.mainOrange}
          borderColor={Colors.mainOrange}
          hoverOpacity={0.8}>
          Add Notes File
        </AddNotesButton>
      ) : null;

    // if no notesUrl, just show the normal helper text
    // otherwise display the uploades notes
    const removeIcon = episode.removingNotes ? 'loader' : 'cross';
    const removeIconExt = removeIcon === 'loader' ? 'gif' : 'png';
    const triggerHelper = isEmpty(notesUrl) ? (
      <div style={styles.addNotesBtnHelperTxt}>{notesUploaderTriggerHelper}</div>
    ) : (
      <div>
        {episode.notesBasename}
        <span style={styles.removeBtn} onClick={this.onRemoveEpisodeNotes(episode)} title="Remove">
          <img src={`../images/status_${removeIcon}.${removeIconExt}`} alt={removeIcon} />
        </span>
      </div>
    );

    return (
      <React.Fragment>
        {triggerBtn}
        {triggerHelper}
      </React.Fragment>
    );
  }

  renderEpisodeDetails(episode, index) {
    const { showNotesUploader, notesUrl } = episode;
    const mainStatusText = episode.public ? 'Public track' : 'Private track';
    const episodeDetailsExtra =
      !showNotesUploader || !isEmpty(notesUrl)
        ? { ...styles.episodeDetailsExtra }
        : { ...styles.episodeDetailsExtra, marginTop: 0 };

    const notesUploader = showNotesUploader ? (
      <div>
        <Smartuploader
          style={{ padding: 0 }}
          fileuploader={{
            server: '/api/uploadv2',
            onprocessfile: this.onEpisodeFileNotesProcess(episode),
            onremovefile: this.onEpisodeFileNotesRemove(episode),
            onerror: this.onEpisodeFileNotesRemove(episode),
            acceptedFileTypes: ['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'],
            fileValidateTypeLabelExpectedTypesMap: {
              'image/png': '.png',
              'image/jpg': '.jpg',
              'image/jpeg': '.jpeg',
              'application/pdf': '.pdf',
            },
            itemInsertInterval: 1000,
            labelIdle: 'Add <span class="filepond--label-action">Notes</span> file',
            fileRenameFunction: file => {
              return `${episode.id}-notes${file.extension}`;
            },
            fileMetadataObject: {
              episodeId: episode.id,
              filerename: `${episode.id}-notes`,
            },
            allowImagePreview: false,
            autobrowsefile: true,
          }}
        />
      </div>
    ) : null;

    return (
      <React.Fragment>
        <div className="episode-identifier">
          Track audio: {episode.originalName} ({episode.id})
        </div>
        <div>
          <ValidatedInput
            type="text"
            styles={styles.inputTitle}
            wrapperStyle={styles.inputTitleWrapper}
            onChange={this.onEpisodeTitleChange(index)}
            value={episode.title}
            validators={[minLengthValidator.bind(null, 1), maxLengthValidator.bind(null, 280)]}
          />
        </div>
        {notesUploader}
        <div style={episodeDetailsExtra}>
          <div style={styles.episodeDetailsExtraStatus}>
            <Toggle
              className="toggle-green"
              id={`episode-${index}-status`}
              aria-labelledby="share-label"
              checked={episode.public}
              onChange={this.onEpisodeStatusChange(index)}
            />
            <span id="share-label" style={{ fontSize: 16, fontWeight: 800, marginLeft: '0.5em' }}>
              {mainStatusText}
            </span>
          </div>
          <div>{this.renderEpisodeUploadFooter(episode)}</div>
        </div>
      </React.Fragment>
    );
  }

  renderExistingEpisodes() {
    const { episodes, uploads } = this.state;
    return (
      <DragDropContext onDragEnd={this.onDragEpisodeEnd}>
        <Droppable droppableId="droppable">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {episodes.map((episode, index) => {
                const isUploading = episode.id in uploads && uploads[episode.id] === 'uploading';
                return (
                  <Draggable
                    draggableId={`episode-idx-${index}`}
                    index={index}
                    key={`episode-key-${index}`}>
                    {dprovided => (
                      <div
                        ref={dprovided.innerRef}
                        {...dprovided.draggableProps}
                        {...dprovided.dragHandleProps}>
                        <RowDivision
                          className="row"
                          style={styles.rowDivisionMini}
                          key={`episode-row-${index}`}>
                          <div className="col-lg-12">
                            <BulkEpisodeItem>
                              <div className="episode-item">
                                <div className="bulk-action">
                                  <i className="material-icons">drag_handle</i>
                                </div>
                                <div className="episode-details">
                                  {this.renderEpisodeDetails(episode, index)}
                                </div>
                              </div>
                              {isUploading && (
                                <LoadingWrapper height="80" float={true}>
                                  <Loading text="Saving Track" />
                                </LoadingWrapper>
                              )}
                            </BulkEpisodeItem>
                          </div>
                        </RowDivision>
                      </div>
                    )}
                  </Draggable>
                );
              })}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }

  renderSaveButtons() {
    const { saving } = this.props;
    let content = (
      <div className="col-lg-12 col-md-12 col-sm-6 col-xs-6" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 17, width: '100%', marginTop: 50 }}>
          <span>Saving</span>
        </div>
        <div className="" style={{ marginTop: 10, width: '100%' }}>
          <Dots color="#727981" size={32} speed={1} />
        </div>
      </div>
    );

    if (!saving) {
      content = (
        <React.Fragment>
          <div className="col-lg-12 col-md-12 col-sm-6 col-xs-6" style={{ textAlign: 'center' }}>
            <div className="btn" style={styles.draftButton} onClick={this.saveEpisodes(false)}>
              <span>Save Draft</span>
            </div>
          </div>
          <div className="col-lg-12 col-md-12 col-sm-6 col-xs-6" style={{ textAlign: 'center' }}>
            <div
              className="btn btn-default"
              style={{ ...styles.draftButton, ...styles.publishButton }}
              onClick={this.saveEpisodes(true)}>
              <span>Publish</span>
            </div>
          </div>
        </React.Fragment>
      );
    }

    return <div style={styles.actionButtonWrapper}>{content}</div>;
  }

  renderAudioUploader() {
    return (
      <Smartuploader
        ref={ref => (this.fileuploaderRef = ref)}
        header={
          <React.Fragment>
            Upload Audio<span style={styles.requiredTxt}>*</span>&nbsp;
            <span style={styles.miniSubheaderText}>(mp3, m4a files accepted)</span>
          </React.Fragment>
        }
        fileuploader={{
          server: {
            process: '/api/uploadv2',
            revert: null,
          },
          beforeAddFile: this.onEpisodeFileAdded,
          onprocessfile: this.onEpisodeFileProcess,
          onremovefile: this.onEpisodeFileRemove,
          onerror: this.onEpisodeFileRemove,
          acceptedFileTypes: ['audio/x-m4a', 'audio/*', 'video/*'],
          fileMetadataObject: {
            bulk: true,
            importingAudio: true,
          },
          allowMultiple: true,
          maxParallelUploads: 2,
          itemInsertLocation: 'before',
          labelIdle: 'Drag & Drop your files or <span class="filepond--label-action">Browse</span>',
        }}
        showImportOption={false}
      />
    );
  }

  render() {
    const { saving, soundcastsManaged } = this.props;
    const { dirty } = this.state;
    return (
      <>
        <RowDivision className="row">
          <div className="col-lg-9 col-md-9 col-sm-12 col-xs-12">
            <div style={styles.soundcastSelectWrapper}>
              <div style={styles.notesLabel}>Publish in</div>
              <Select
                value={this.state.soundcastId}
                style={styles.soundcastSelect}
                onChange={this.onChangeSoundcastId}>
                {isEmpty(soundcastsManaged) && <option>Loading...</option>}
                {soundcastsManaged.map((soundcast, i) => {
                  return (
                    <option value={soundcast.id} key={i}>
                      {soundcast.title}
                    </option>
                  );
                })}
              </Select>
            </div>
          </div>
        </RowDivision>
        <RowDivision className="row">
          <div className="col-lg-9 col-md-9 col-sm-12 col-xs-12">
            {(!isEmpty(soundcastsManaged) || !isEmpty(this.state.episodes)) &&
              this.renderAudioUploader()}
            {isEmpty(soundcastsManaged) && (
              <div
                className="text-large"
                style={{ ...styles.plainWhiteWrapper, padding: '10px 20px' }}>
                Waiting for user soundcasts...
              </div>
            )}
          </div>
        </RowDivision>
        <RowDivision className="row" id="episode-stage">
          <div className="col-lg-9 col-md-9 col-sm-12 col-xs-12">
            {this.renderExistingEpisodes()}
          </div>
        </RowDivision>
        {this.renderSaveButtons()}
        <BeforeUnload dirty={dirty || saving} />
      </>
    );
  }
}

const BulkEpisodeItem = styled.div`
  background-color: ${Colors.mainWhite};
  padding: 20px;
  border-radius: 10px;
  position: relative;

  .episode-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-right: 20px;

    .bulk-action {
      padding: 25px 20px;
      padding-left: 0px;

      i {
        font-size: 3rem;
      }
    }

    .episode-details {
      width: 100%;
      cursor: auto;

      .episode-identifier {
        text-align: right;
        font-weight: 600;
        padding-bottom: 5px;
        margin-top: -5px;
      }
    }
  }
`;

const AddNotesButton = styled(BaseButton)`
  text-transform: uppercase;
  border-radius: 14px;
`;

const styles = {
  ...ostyles,
  rowDivisionMini: {
    padding: '5px 0',
  },
  episodeDetailsExtra: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  episodeDetailsExtraStatus: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  addNotesBtnHelperTxt: {
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#919191',
  },
  removeBtn: {
    color: '#e52828',
    fontWeight: 600,
    cursor: 'pointer',
    marginLeft: 10,
  },

  actionButtonWrapper: {
    position: 'fixed',
    top: '15rem',
    right: 0,
    width: 270,
  },
};

BulkEpisode.propTypes = {
  userInfo: PropTypes.object,
  soundcastsManaged: PropTypes.array,
  realSaveEpisode: PropTypes.func,
  onDeleteEpisode: PropTypes.func,
  hasExceededPlanLimit: PropTypes.func,
  modalHandler: PropTypes.func,
  saving: PropTypes.bool,
  proUser: PropTypes.bool,
};

BulkEpisode.defaultProps = {
  userInfo: {},
  soundcastsManaged: [],
  realSaveEpisode: () => {},
  onDeleteEpisode: () => {},
  hasExceededPlanLimit: () => {},
  modalHandler: () => {},
  saving: false,
  proUser: false,
};

export default BulkEpisode;
